import { router } from "../router/router.js";
import { contextStore } from "../auth/context-store.js";
import { platform } from "../../app/browser-platform.js";
import type { Company } from "../../domain/identity/company.js";
import { CompanyStatus, isValidCnpj, maskCnpj, formatCnpj } from "../../domain/identity/company.js";

export default class AdminCompaniesPage extends HTMLElement {
  private companies: Company[] = [];
  private searchQuery = "";
  private statusFilter = "ALL";
  private isModalOpen = false;
  private editingCompany: Company | null = null;
  private inactivateModal = { isOpen: false, id: "", tradeName: "" };

  public connectedCallback(): void {
    this.loadData();
  }

  private async loadData(): Promise<void> {
    try {
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      this.companies = await platform.adminService.listCompanies(dummyContext);
      this.render();
    } catch (e) {
      console.error("Failed to load companies", e);
    }
  }

  private render(): void {
    const filtered = this.companies.filter(c => {
      const matchesStatus = this.statusFilter === "ALL" || c.status === this.statusFilter;
      const term = this.searchQuery.toLowerCase();
      const matchesSearch = !term ||
        c.tradeName.toLowerCase().includes(term) ||
        c.legalName.toLowerCase().includes(term) ||
        c.cnpj.includes(term) ||
        c.normalizedCnpj.includes(term);
      return matchesStatus && matchesSearch;
    });

    this.innerHTML = `
      <div style="display: flex; flex-direction: column; height: 100%; gap: 16px; background: var(--color-bg-primary); padding: 24px;">
        <header style="display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid var(--color-graphite-200); padding-bottom: 16px;">
          <div>
            <h1 style="color: var(--color-text-primary); font-size: 1.5rem; font-weight: 700; margin: 0;">Gestão de Empresas & Prestadoras</h1>
            <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin-top: 4px;">Cadastro de empresas parceiras, fornecedores e prestadoras com homologação de CNPJ válido.</p>
          </div>
          <button id="btn-new-company" style="background: var(--ia-action-bg, var(--color-primary)); color: var(--ia-action-fg, #F7F8F8); border: none; padding: 8px 16px; border-radius: 6px; font-weight: 600; cursor: pointer; font-size: 0.875rem; display: flex; align-items: center; gap: 6px;">
            <span>+ Nova Empresa</span>
          </button>
        </header>

        <!-- Filters -->
        <div style="display: flex; gap: 12px; background: var(--color-surface-primary); padding: 16px; border-radius: 8px; border: 1px solid var(--color-graphite-200); align-items: center;">
          <input type="text" id="input-search" placeholder="Buscar por Razão Social, Nome Fantasia ou CNPJ..." value="${this.searchQuery}" style="flex: 1; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
          <select id="select-status" style="padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem;">
            <option value="ALL" ${this.statusFilter === "ALL" ? "selected" : ""}>Todos os Status</option>
            <option value="ACTIVE" ${this.statusFilter === "ACTIVE" ? "selected" : ""}>Ativas</option>
            <option value="INACTIVE" ${this.statusFilter === "INACTIVE" ? "selected" : ""}>Inativas</option>
          </select>
        </div>

        <!-- Data Table -->
        <div style="flex: 1; background: var(--color-surface-primary); border-radius: 8px; border: 1px solid var(--color-graphite-200); overflow-y: auto;">
          <table style="width: 100%; border-collapse: collapse; text-align: left; font-size: 0.875rem;">
            <thead style="background: var(--color-surface-secondary); border-bottom: 1px solid var(--color-graphite-200); position: sticky; top: 0;">
              <tr>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">NOME FANTASIA</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">RAZÃO SOCIAL</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">CNPJ DA EMPRESA</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600;">STATUS</th>
                <th style="padding: 12px 16px; color: var(--color-text-secondary); font-weight: 600; text-align: right;">AÇÕES</th>
              </tr>
            </thead>
            <tbody>
              ${filtered.length === 0 ? `
                <tr>
                  <td colspan="5" style="padding: 32px; text-align: center; color: var(--color-graphite-400);">Nenhuma empresa cadastrada.</td>
                </tr>
              ` : filtered.map(c => `
                <tr style="border-bottom: 1px solid var(--color-bg-secondary);">
                  <td style="padding: 12px 16px; font-weight: 600; color: var(--ia-primary);">${c.tradeName}</td>
                  <td style="padding: 12px 16px; color: var(--color-text-secondary);">${c.legalName}</td>
                  <td style="padding: 12px 16px; font-family: monospace; color: var(--color-ink); font-weight: 500;">${formatCnpj(c.cnpj)}</td>
                  <td style="padding: 12px 16px;">
                    <span style="background: ${c.status === CompanyStatus.Active ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)'}; color: ${c.status === CompanyStatus.Active ? 'var(--status-success-solid)' : 'var(--status-danger-solid)'}; padding: 2px 8px; border-radius: 12px; font-size: 0.75rem; font-weight: 600;">
                      ${c.status === CompanyStatus.Active ? 'ATIVA' : 'INATIVA'}
                    </span>
                  </td>
                  <td style="padding: 12px 16px; text-align: right;">
                    <button class="btn-edit" data-id="${c.id}" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--color-text-muted); margin-right: 4px;">Editar</button>
                    ${c.status === CompanyStatus.Active ? `
                      <button class="btn-inactivate" data-id="${c.id}" data-name="${c.tradeName}" style="background: var(--status-danger-bg); border: 1px solid var(--status-danger-border); border-radius: 4px; padding: 4px 8px; font-size: 0.75rem; cursor: pointer; color: var(--status-danger-text);">Inativar</button>
                    ` : ''}
                  </td>
                </tr>
              `).join("")}
            </tbody>
          </table>
        </div>

        ${this.isModalOpen ? this.renderModal() : ""}
        ${this.inactivateModal.isOpen ? this.renderInactivateModal() : ""}
      </div>
    `;

    this.attachEvents();
  }

  private renderModal(): string {
    const isEdit = !!this.editingCompany;
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 500px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
            <h2 style="font-size: 1.25rem; font-weight: 700; color: var(--color-text-muted); margin: 0;">${isEdit ? 'Editar Empresa' : 'Cadastro da Empresa'}</h2>
            <button id="btn-close-modal" style="background: none; border: none; font-size: 1.25rem; cursor: pointer; color: var(--color-text-secondary);">✕</button>
          </div>
          <div id="modal-error-banner" style="display: none; margin-bottom: 16px; background: var(--status-danger-bg); border: 1px solid var(--status-danger-bg); color: var(--status-danger-text); padding: 10px 14px; border-radius: 6px; font-size: 0.8125rem; line-height: 1.4;"></div>
          <form id="form-company" style="display: flex; flex-direction: column; gap: 16px;">
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Razão Social *</label>
              <input type="text" id="modal-legal-name" value="${this.editingCompany?.legalName || ''}" required placeholder="Ex: Prestadora Industrial Ltda" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            <div>
              <label style="display: block; font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted); margin-bottom: 4px;">Nome Fantasia *</label>
              <input type="text" id="modal-trade-name" value="${this.editingCompany?.tradeName || ''}" required placeholder="Ex: Prestadora NDT" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box;">
            </div>
            <div>
              <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px;">
                <label for="modal-cnpj" style="font-size: 0.8125rem; font-weight: 600; color: var(--color-text-muted);">CNPJ da Empresa *</label>
                <span id="cnpj-status-indicator" style="font-size: 0.75rem; font-weight: 600;"></span>
              </div>
              <input type="text" id="modal-cnpj" value="${this.editingCompany?.cnpj ? formatCnpj(this.editingCompany.cnpj) : ''}" required placeholder="00.000.000/0000-00" maxlength="18" style="width: 100%; padding: 8px 12px; border: 1px solid var(--color-graphite-300); border-radius: 6px; font-size: 0.875rem; box-sizing: border-box; font-family: monospace;">
              <span style="display: block; font-size: 0.75rem; color: var(--color-text-secondary); margin-top: 4px;">Obrigatório: informe um CNPJ válido com 14 dígitos e verificação matemática de dígitos verificadores.</span>
            </div>
            <div style="display: flex; justify-content: flex-end; gap: 8px; margin-top: 8px;">
              <button type="button" id="btn-cancel-modal" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
              <button type="submit" style="background: var(--ia-action-bg, var(--color-primary)); border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 600; color: var(--ia-action-fg, #F7F8F8); cursor: pointer;">Salvar</button>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  private renderInactivateModal(): string {
    return `
      <div style="position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 9999; backdrop-filter: blur(2px);">
        <div style="background: var(--color-surface-primary); border-radius: 12px; width: 100%; max-width: 440px; padding: 24px; box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);">
          <h3 style="font-size: 1.125rem; font-weight: 700; color: var(--color-text-muted); margin: 0 0 8px 0;">Inativar Empresa</h3>
          <p style="color: var(--color-text-secondary); font-size: 0.875rem; margin: 0 0 20px 0;">Deseja realmente inativar a empresa <strong>${this.inactivateModal.tradeName}</strong>?</p>
          <div style="display: flex; justify-content: flex-end; gap: 8px;">
            <button id="btn-cancel-inactivate" style="background: var(--color-surface-secondary); border: 1px solid var(--color-graphite-300); border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 500; cursor: pointer; color: var(--color-text-secondary);">Cancelar</button>
            <button id="btn-confirm-inactivate" style="background: var(--status-danger-text); border: none; border-radius: 6px; padding: 8px 16px; font-size: 0.875rem; font-weight: 600; color: var(--color-text-primary); cursor: pointer;">Confirmar Inativação</button>
          </div>
        </div>
      </div>
    `;
  }

  private attachEvents(): void {
    this.querySelector("#input-search")?.addEventListener("input", (e) => {
      this.searchQuery = (e.target as HTMLInputElement).value;
      this.render();
    });

    this.querySelector("#select-status")?.addEventListener("change", (e) => {
      this.statusFilter = (e.target as HTMLSelectElement).value;
      this.render();
    });

    this.querySelector("#btn-new-company")?.addEventListener("click", () => {
      this.editingCompany = null;
      this.isModalOpen = true;
      this.render();
    });

    this.querySelectorAll(".btn-edit").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id");
        this.editingCompany = this.companies.find(c => c.id === id) || null;
        this.isModalOpen = true;
        this.render();
      });
    });

    this.querySelectorAll(".btn-inactivate").forEach(btn => {
      btn.addEventListener("click", (e) => {
        const id = (e.currentTarget as HTMLElement).getAttribute("data-id") || "";
        const tradeName = (e.currentTarget as HTMLElement).getAttribute("data-name") || "";
        this.inactivateModal = { isOpen: true, id, tradeName };
        this.render();
      });
    });

    this.querySelector("#btn-close-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-cancel-modal")?.addEventListener("click", () => {
      this.isModalOpen = false;
      this.render();
    });

    this.querySelector("#btn-cancel-inactivate")?.addEventListener("click", () => {
      this.inactivateModal = { isOpen: false, id: "", tradeName: "" };
      this.render();
    });

    this.querySelector("#btn-confirm-inactivate")?.addEventListener("click", async () => {
      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;
      try {
        await platform.adminService.inactivateCompany(dummyContext, this.inactivateModal.id);
        this.inactivateModal = { isOpen: false, id: "", tradeName: "" };
        await this.loadData();
      } catch (err: any) {
        console.error("Failed to inactivate company", err);
      }
    });

    const cnpjInput = this.querySelector("#modal-cnpj") as HTMLInputElement;
    const statusIndicator = this.querySelector("#cnpj-status-indicator") as HTMLElement;
    if (cnpjInput) {
      const updateStatus = () => {
        const rawVal = cnpjInput.value.trim();
        if (!rawVal) {
          if (statusIndicator) statusIndicator.innerHTML = "";
          cnpjInput.style.borderColor = "var(--color-graphite-300)";
          return;
        }
        const valid = isValidCnpj(rawVal);
        if (valid) {
          if (statusIndicator) {
            statusIndicator.innerHTML = `<span style="color: var(--status-success-text); font-weight: 600;">✓ CNPJ Válido</span>`;
          }
          cnpjInput.style.borderColor = "var(--status-success-solid)";
        } else {
          const digits = rawVal.replace(/\D/g, "");
          if (digits.length >= 14) {
            if (statusIndicator) {
              statusIndicator.innerHTML = `<span style="color: var(--status-danger-solid); font-weight: 600;">✕ CNPJ Inválido</span>`;
            }
            cnpjInput.style.borderColor = "var(--status-danger-solid)";
          } else {
            if (statusIndicator) {
              statusIndicator.innerHTML = `<span style="color: var(--status-warning-text); font-weight: 500;">${digits.length}/14 dígitos</span>`;
            }
            cnpjInput.style.borderColor = "var(--color-graphite-300)";
          }
        }
      };

      cnpjInput.addEventListener("input", (e) => {
        const input = e.target as HTMLInputElement;
        input.value = maskCnpj(input.value);
        updateStatus();
      });

      // Initial check on modal load
      updateStatus();
    }

    this.querySelector("#form-company")?.addEventListener("submit", async (e) => {
      e.preventDefault();
      const legalName = (this.querySelector("#modal-legal-name") as HTMLInputElement).value;
      const tradeName = (this.querySelector("#modal-trade-name") as HTMLInputElement).value;
      const cnpj = (this.querySelector("#modal-cnpj") as HTMLInputElement).value;

      const errorBanner = this.querySelector("#modal-error-banner") as HTMLElement;
      if (errorBanner) errorBanner.style.display = "none";

      if (!isValidCnpj(cnpj)) {
        if (errorBanner) {
          errorBanner.textContent = "CNPJ inválido. Por favor, informe um CNPJ válido com 14 dígitos e verificação matemática correta.";
          errorBanner.style.display = "block";
        }
        return;
      }

      const ctx = contextStore.get();
      const dummyContext = {
        activeClientId: ctx.activeClientId,
        permissions: ctx.permissions,
        correlationId: "",
        userId: ctx.userId
      } as any;

      try {
        if (this.editingCompany) {
          await platform.adminService.updateCompany(dummyContext, {
            ...this.editingCompany,
            legalName,
            tradeName,
            cnpj
          });
        } else {
          await platform.adminService.createCompany(dummyContext, {
            legalName,
            tradeName,
            cnpj
          });
        }
        this.isModalOpen = false;
        await this.loadData();
      } catch (err: any) {
        if (errorBanner) {
          errorBanner.textContent = err.message || "Erro ao salvar empresa.";
          errorBanner.style.display = "block";
        } else {
          console.error(err);
        }
      }
    });
  }
}

if (!customElements.get("app-admin-companies-page")) {
  customElements.define("app-admin-companies-page", AdminCompaniesPage);
}



